/**
 * SchedulesPage — Manages inspection schedules linking templates to machines.
 *
 * Displays a FilterableDataTable with columns: Template, Machine, Frequency,
 * Next Run, Active, Actions. Supports create/edit via DrawerPanel and
 * toggle active/inactive with a single click.
 *
 * @see Requirements 7.1, 7.2, 7.3, 7.4, 7.5, 7.6
 */

import React, { useState, useCallback, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { GridColDef } from '@mui/x-data-grid';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import FormControlLabel from '@mui/material/FormControlLabel';
import Switch from '@mui/material/Switch';
import EditIcon from '@mui/icons-material/Edit';
import PowerSettingsNewIcon from '@mui/icons-material/PowerSettingsNew';
import DeleteIcon from '@mui/icons-material/Delete';

import {
  FilterableDataTable,
  DrawerPanel,
  ConfirmDialog,
  PageContainer,
} from '../../components/shared';
import type { FilterConfig, RowAction, DrawerAction } from '../../components/shared';
import {
  InspectionSchedulesApi,
  MachineControllerApi,
  ChecklistTemplateControllerApi,
} from '../../api-generated/apis';
import type {
  Machine,
  ChecklistTemplate,
  CreateScheduleRequest,
} from '../../api-generated/models';
import { CreateScheduleRequestFrequencyEnum } from '../../api-generated/models';
import { apiConfig } from '../../api-generated/apiConfig';

const schedulesApi = new InspectionSchedulesApi(apiConfig);
const machineApi = new MachineControllerApi(apiConfig);
const templateApi = new ChecklistTemplateControllerApi(apiConfig);

// ─── Types ───────────────────────────────────────────────────────────────────

type ScheduleFrequency = 'DAILY' | 'WEEKLY' | 'BIWEEKLY' | 'MONTHLY';

interface Schedule {
  id: string;
  templateId: string;
  templateName: string;
  machineId: string;
  machineName: string;
  frequency: ScheduleFrequency;
  nextRun: string;
  active: boolean;
}

interface ScheduleFormData {
  templateId: string;
  machineId: string;
  frequency: ScheduleFrequency | '';
  active: boolean;
}

interface TemplateOption {
  id: string;
  name: string;
}

interface MachineOption {
  id: string;
  name: string;
}

// ─── Constants ───────────────────────────────────────────────────────────────

const FREQUENCY_OPTIONS: { value: ScheduleFrequency; label: string }[] = [
  { value: 'DAILY', label: 'Daily' },
  { value: 'WEEKLY', label: 'Weekly' },
  { value: 'BIWEEKLY', label: 'Biweekly' },
  { value: 'MONTHLY', label: 'Monthly' },
];

const INITIAL_FORM: ScheduleFormData = {
  templateId: '',
  machineId: '',
  frequency: '',
  active: true,
};

// ─── Component ───────────────────────────────────────────────────────────────

const SchedulesPage: React.FC = () => {
  const queryClient = useQueryClient();

  // ─── Pagination state ──────────────────────────────────────────────────
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);

  // ─── Drawer state ──────────────────────────────────────────────────────
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerMode, setDrawerMode] = useState<'create' | 'edit'>('create');
  const [editingSchedule, setEditingSchedule] = useState<Schedule | null>(null);
  const [formData, setFormData] = useState<ScheduleFormData>(INITIAL_FORM);

  // ─── Delete confirmation state ─────────────────────────────────────────
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [scheduleToDelete, setScheduleToDelete] = useState<Schedule | null>(null);

  // ─── Queries ───────────────────────────────────────────────────────────

  const schedulesQuery = useQuery<any>({
    queryKey: ['schedules', page, pageSize],
    queryFn: () => schedulesApi.getAllSchedules({}),
  });

  const templatesQuery = useQuery<ChecklistTemplate[]>({
    queryKey: ['templates-options'],
    queryFn: () => templateApi.getAllTemplates({}),
    staleTime: 60_000,
  });

  const machinesQuery = useQuery<Machine[]>({
    queryKey: ['machines-options'],
    queryFn: () => machineApi.getAllMachines(),
    staleTime: 60_000,
  });

  // Transform template data to options
  const templateOptions: TemplateOption[] = useMemo(
    () => (templatesQuery.data ?? []).map((t: any) => ({ id: t.id ?? '', name: t.name ?? '' })),
    [templatesQuery.data],
  );

  // Transform machine data to options
  const machineOptions: MachineOption[] = useMemo(
    () => (machinesQuery.data ?? []).map((m: any) => ({ id: m.id ?? '', name: m.name ?? '' })),
    [machinesQuery.data],
  );

  // Map raw schedule response to Schedule rows
  const scheduleRows: Schedule[] = useMemo(() => {
    const raw = schedulesQuery.data;
    // Handle both array and paginated response shapes
    const items: any[] = Array.isArray(raw) ? raw : raw?.content ?? [];
    return items.map((item: any) => ({
      id: item.id ?? '',
      templateId: item.templateId ?? '',
      templateName: item.templateName ?? templateOptions.find((t) => t.id === item.templateId)?.name ?? '',
      machineId: item.machineId ?? '',
      machineName: item.machineName ?? machineOptions.find((m) => m.id === item.machineId)?.name ?? '',
      frequency: item.frequency ?? 'DAILY',
      nextRun: item.nextGenerationAt ?? item.nextRun ?? '',
      active: item.active ?? false,
    }));
  }, [schedulesQuery.data, templateOptions, machineOptions]);

  const totalCount = scheduleRows.length;

  // ─── Mutations ─────────────────────────────────────────────────────────

  const createMutation = useMutation({
    mutationFn: (data: CreateScheduleRequest) =>
      schedulesApi.createSchedule({ createScheduleRequest: data }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['schedules'] });
      handleCloseDrawer();
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: CreateScheduleRequest }) =>
      schedulesApi.updateSchedule({ id, createScheduleRequest: data }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['schedules'] });
      handleCloseDrawer();
    },
  });

  const toggleMutation = useMutation({
    mutationFn: ({ id, active }: { id: string; active: boolean }) =>
      schedulesApi.setScheduleActive({ id, active }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['schedules'] });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => schedulesApi.deleteSchedule({ id }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['schedules'] });
      setDeleteDialogOpen(false);
      setScheduleToDelete(null);
    },
  });

  // ─── Drawer handlers ───────────────────────────────────────────────────

  const handleOpenCreate = useCallback(() => {
    setFormData(INITIAL_FORM);
    setEditingSchedule(null);
    setDrawerMode('create');
    setDrawerOpen(true);
  }, []);

  const handleOpenEdit = useCallback((schedule: Schedule) => {
    setFormData({
      templateId: schedule.templateId,
      machineId: schedule.machineId,
      frequency: schedule.frequency,
      active: schedule.active,
    });
    setEditingSchedule(schedule);
    setDrawerMode('edit');
    setDrawerOpen(true);
  }, []);

  const handleCloseDrawer = useCallback(() => {
    setDrawerOpen(false);
    setEditingSchedule(null);
    setFormData(INITIAL_FORM);
  }, []);

  const handleSubmit = useCallback(() => {
    if (!formData.templateId || !formData.machineId || !formData.frequency) return;

    const payload: CreateScheduleRequest = {
      templateId: String(formData.templateId),
      machineId: String(formData.machineId),
      frequency: formData.frequency as CreateScheduleRequestFrequencyEnum,
    };

    if (drawerMode === 'edit' && editingSchedule) {
      updateMutation.mutate({ id: String(editingSchedule.id), data: payload });
    } else {
      createMutation.mutate(payload);
    }
  }, [formData, drawerMode, editingSchedule, updateMutation, createMutation]);

  // ─── Toggle active handler ─────────────────────────────────────────────

  const handleToggleActive = useCallback(
    (schedule: Schedule) => {
      toggleMutation.mutate({ id: String(schedule.id), active: !schedule.active });
    },
    [toggleMutation],
  );

  // ─── Delete handlers ───────────────────────────────────────────────────

  const handleRequestDelete = useCallback((schedule: Schedule) => {
    setScheduleToDelete(schedule);
    setDeleteDialogOpen(true);
  }, []);

  const handleConfirmDelete = useCallback(() => {
    if (scheduleToDelete) {
      deleteMutation.mutate(String(scheduleToDelete.id));
    }
  }, [scheduleToDelete, deleteMutation]);

  // ─── Table configuration ───────────────────────────────────────────────

  const columns: GridColDef[] = useMemo(
    () => [
      { field: 'templateName', headerName: 'Template', flex: 1, minWidth: 150 },
      { field: 'machineName', headerName: 'Machine', flex: 1, minWidth: 150 },
      {
        field: 'frequency',
        headerName: 'Frequency',
        width: 120,
        renderCell: (params) => {
          const opt = FREQUENCY_OPTIONS.find((o) => o.value === params.value);
          return opt?.label ?? params.value;
        },
      },
      {
        field: 'nextRun',
        headerName: 'Next Run',
        width: 180,
        renderCell: (params) => {
          if (!params.value) return '—';
          return new Date(params.value).toLocaleString();
        },
      },
      {
        field: 'active',
        headerName: 'Active',
        width: 100,
        renderCell: (params) => (
          <Chip
            label={params.value ? 'Active' : 'Inactive'}
            color={params.value ? 'success' : 'default'}
            size="small"
            variant="outlined"
          />
        ),
      },
    ],
    [],
  );

  const filters: FilterConfig[] = useMemo(() => {
    const machineFilterOptions = machineOptions.map((m) => ({
      value: String(m.id),
      label: m.name,
    }));

    return [
      {
        field: 'machine',
        label: 'Machine',
        type: 'select' as const,
        options: machineFilterOptions,
      },
      {
        field: 'frequency',
        label: 'Frequency',
        type: 'select' as const,
        options: FREQUENCY_OPTIONS.map((o) => ({ value: o.value, label: o.label })),
      },
      {
        field: 'active',
        label: 'Active',
        type: 'select' as const,
        options: [
          { value: 'true', label: 'Active' },
          { value: 'false', label: 'Inactive' },
        ],
      },
    ];
  }, [machineOptions]);

  const rowActions: RowAction<Schedule>[] = useMemo(
    () => [
      {
        label: 'Edit',
        icon: EditIcon,
        onClick: handleOpenEdit,
      },
      {
        label: 'Toggle Active',
        icon: PowerSettingsNewIcon,
        onClick: handleToggleActive,
      },
      {
        label: 'Delete',
        icon: DeleteIcon,
        onClick: handleRequestDelete,
      },
    ],
    [handleOpenEdit, handleToggleActive, handleRequestDelete],
  );

  // ─── Drawer actions ────────────────────────────────────────────────────

  const drawerActions: DrawerAction[] = useMemo(
    () => [
      {
        label: 'Cancel',
        variant: 'outlined' as const,
        onClick: handleCloseDrawer,
      },
      {
        label: drawerMode === 'edit' ? 'Update' : 'Create',
        variant: 'contained' as const,
        onClick: handleSubmit,
        disabled:
          !formData.templateId || !formData.machineId || !formData.frequency ||
          createMutation.isPending || updateMutation.isPending,
      },
    ],
    [handleCloseDrawer, handleSubmit, drawerMode, formData, createMutation.isPending, updateMutation.isPending],
  );

  // ─── Render ────────────────────────────────────────────────────────────

  return (
    <PageContainer title="Schedules" subtitle="Manage inspection schedules">
      <FilterableDataTable<Schedule>
        rows={scheduleRows}
        columns={columns}
        loading={schedulesQuery.isLoading}
        totalCount={totalCount}
        searchPlaceholder="Search schedules…"
        filters={filters}
        page={page}
        pageSize={pageSize}
        onPageChange={setPage}
        onPageSizeChange={setPageSize}
        actions={rowActions}
        primaryAction={{ label: 'New Schedule', onClick: handleOpenCreate }}
      />

      {/* Create/Edit Drawer */}
      <DrawerPanel
        open={drawerOpen}
        onClose={handleCloseDrawer}
        title={drawerMode === 'edit' ? 'Edit Schedule' : 'New Schedule'}
        width="narrow"
        actions={drawerActions}
        loading={createMutation.isPending || updateMutation.isPending}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          {/* Template Picker */}
          <TextField
            select
            label="Template"
            value={formData.templateId}
            onChange={(e) =>
              setFormData((prev) => ({ ...prev, templateId: e.target.value }))
            }
            fullWidth
            required
            disabled={templatesQuery.isLoading}
          >
            {templateOptions.map((t) => (
              <MenuItem key={t.id} value={t.id}>
                {t.name}
              </MenuItem>
            ))}
          </TextField>

          {/* Machine Picker */}
          <TextField
            select
            label="Machine"
            value={formData.machineId}
            onChange={(e) =>
              setFormData((prev) => ({ ...prev, machineId: e.target.value }))
            }
            fullWidth
            required
            disabled={machinesQuery.isLoading}
          >
            {machineOptions.map((m) => (
              <MenuItem key={m.id} value={m.id}>
                {m.name}
              </MenuItem>
            ))}
          </TextField>

          {/* Frequency Selector */}
          <TextField
            select
            label="Frequency"
            value={formData.frequency}
            onChange={(e) =>
              setFormData((prev) => ({
                ...prev,
                frequency: e.target.value as ScheduleFrequency,
              }))
            }
            fullWidth
            required
          >
            {FREQUENCY_OPTIONS.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>
                {opt.label}
              </MenuItem>
            ))}
          </TextField>

          {/* Active Toggle */}
          <FormControlLabel
            control={
              <Switch
                checked={formData.active}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, active: e.target.checked }))
                }
              />
            }
            label="Active"
          />
        </Box>
      </DrawerPanel>

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        open={deleteDialogOpen}
        onClose={() => {
          setDeleteDialogOpen(false);
          setScheduleToDelete(null);
        }}
        onConfirm={handleConfirmDelete}
        title="Delete Schedule"
        message={
          scheduleToDelete
            ? `Are you sure you want to delete the schedule for "${scheduleToDelete.templateName}" on "${scheduleToDelete.machineName}"? This action cannot be undone.`
            : 'Are you sure you want to delete this schedule?'
        }
        confirmLabel="Delete"
        confirmColor="error"
      />
    </PageContainer>
  );
};

export default SchedulesPage;
